<template>
  <li class="notification" :class="{ 'is-open': isOpen }" ref="notifDropdown" @focusout="handleClickOutside">
    <button type="button" class="btn-icon notification-dropdown-btn" aria-label="Notification" title="Notification" :aria-expanded="isOpen.toString()" aria-controls="notification-dropdown-menu" @click="toggleOpen">
      <i class="fas fa-bell" aria-hidden="true"></i>
      <span v-if="unreadNotifications && unreadNotifications.length > 0" class="count-bubble">
        {{ unreadNotifications.length }}
      </span>
    </button>
    <span class="dropdown-container-arrow"></span>
    <ul v-if="notifications && notifications.length > 0" id="notification-dropdown-menu" class="dropdown k-menu-panel" :class="{ 'is-open': isOpen }" :hidden="!isOpen">
      <button v-if="unreadNotifications && unreadNotifications.length > 0" type="button" class="btn btn-tertiary mark-all-as-read-btn" @click="readAllNotifications">Mark All as Read</button>
      <li v-for="n in notificationSample"
        :key="n.notification_id || n.id"
        class="notification-item k-menu-item"
        :class="{ unread: !n.read_at }">
        <router-link
          v-if="isRoutable(n)"
          :to="getThreadRoute(n)"
          @click="markNotificationAsRead(n)">
          <div class="notification-item-content">
            <span class="subject" v-html="$kpurify.sanitise(n.subject)"></span>
            <span v-if="!n.read_at" class="new-label">New</span>
          </div>
          <span class="body">{{ n.body }}</span>
          <span class="post-time">{{ parseTimestamp(n.posted_at) }}</span>
        </router-link>
        <div class="no-link-notification" v-else>
          <span class="subject" v-html="$kpurify.sanitise(n.subject)"></span>
          <span v-if="!n.read_at" class="new-label">New</span>
          <span class="body">{{ n.body }}</span>
          <span class="post-time">{{ parseTimestamp(n.posted_at) }}</span>
        </div>
      </li>
      <li class="show-more k-menu-item" v-if="showMoreButtonVisible">
        <button class="show-more-btn btn btn-primary" @click="showMore()">Show More...</button>
      </li>
    </ul>
  </li>
</template>

<style scoped>
.notification-dropdown-btn > i {
  position: relative;
}

i.fas.fa-bell {
  padding: 0 5px;
  margin: 0;
  font-size: 1.2em;
}

.count-bubble {
  position: absolute;
  color: var(--kate-type-white);
  background-color: var(--kate-danger);
  top: 2px;
  right: 0;
  font-size: 0.7em;
  margin: 0;
}

li.notification-item.unread {
  border-bottom: 1px solid var(--kate-secondary);
  border-top: 1px solid var(--kate-secondary);
  background-color: var(--kate-background-body);
}

.no-link-notification {
  padding: 10px;
}

.no-link-notification:hover {
  background-color: var(--kate-background-body-alpha);
}

.no-link-notification > span.subject {
  color: var(--kate-mono-0);
}

li.notification-item > a > span,
li.notification-item > .no-link-notification > span {
  display: block;
  line-height: 1.5;
  text-align: left;
}

li.notification-item > a > span.body,
li.notification-item > a > span.post-time,
li.notification-item > .no-link-notification > span.body,
li.notification-item > .no-link-notification > span.post-time {
  font-size: 0.8em;
  color: var(--kate-type-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

li.notification-item > a > span.post-time,
li.notification-item > .no-link-notification > span.post-time {
  color: var(--kate-type-primary);
}

span.new-label {
  width: min-content;
  font-size: 0.9em;
  padding: 0 5px;
  margin-left: 5px;
  border-radius: 5px;
  background-color: var(--kate-success);
  color: var(--kate-type-dark);
}

ul.dropdown {
  padding-bottom: 0 !important;
}

.dropdown button {
  position: relative;
  display: block;
  width: 100%;
  height: 50px;
  border: 0;
  padding: 8px 15px;
  border-radius: 0;
  line-height: unset !important;
}

.show-more > button.show-more-btn {
  color: var(--kate-type-white) !important;
  background-color: var(--kate-button-tertiary);
}

.show-more > button.show-more-btn:hover {
  background-color: var(--kate-button-tertiary-hover);
  color: var(--kate-type-white) !important;
}

[data-theme="light"] .dropdown > button.mark-all-as-read-btn {
  color: var(--kate-type-white) !important;
}
</style>

<style>
.k-navbar li.notification-item.unread a {
  background-color: var(--kate-notification-unread);
}

.k-navbar li.notification-item.unread:hover a {
  background-color: var(--kate-notification-unread-hover);
}

.k-navbar li.notification-item.unread .subject {
  font-family: hk-grotesk-semibold, sans-serif;
  text-decoration: underline;
}

.k-navbar li.notification-item.unread a:hover > span.subject {
  color: var(--kate-type-link);
}

.k-navbar li.notification-item.unread .subject b {
  color: var(--kate-secondary);
}

.k-navbar li.notification-item .subject b {
  color: var(--kate-type-warning);
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import notificationsStore from '@stores/notifications-store';

const DASHBOARD_HIDDEN_TYPES = new Set([
  'referred_otj_entry',
  'approved_otj_entry',
]);

export default {
  mixins: [TimeMixin],

  props: {
    isOpen: {
      type: Boolean,
    },
    isDashboard: {
      type: Boolean,
    },
    isLoggedIn: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      notificationsStore: notificationsStore(),
      nNotificationsShown: undefined,
      nExtraNotificationIncrements: 10,
    };
  },

  beforeMount() {
    this.nNotificationsShown = this.nExtraNotificationIncrements;
  },

  mounted() {
    if (this.isLoggedIn) {
      this.notificationsStore.startNotificationRefreshCycle();
      this.notificationsStore.getNotifications();
    }
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    this.notificationsStore.stopNotificationRefreshCycle();
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    route() {
      // Close if ever the route changes
      this.onClose();
    },
    isOpen() {
      if (!this.isOpen) {
        this.nNotificationsShown = this.nExtraNotificationIncrements;
      }
    },
    isLoggedIn() {
      this.showUserDropdown = false;
      if (this.isLoggedIn) {
        this.notificationsStore.getNotifications();
        this.notificationsStore.startNotificationRefreshCycle();
      }
      if (!this.isLoggedIn) {
        this.notificationsStore.clear();
      }
    },
  },

  computed: {
    route() {
      return this.$route;
    },

    notifications() {
      return this.notificationsStore.notifications;
    },

    filteredNotifications() {
      if (!this.notifications.length) {
        return [];
      }
      if (!this.isDashboard) {
        return this.notifications;
      }
      return this.notifications.filter(
        n => !DASHBOARD_HIDDEN_TYPES.has(n.notification_type),
      );
    },

    unreadNotifications() {
      if (!this.filteredNotifications) {
        return [];
      }
      return this.filteredNotifications.filter(x => !x.read_at);
    },

    notificationSample() {
      return this.filteredNotifications.slice(0, this.nNotificationsShown);
    },
    showMoreButtonVisible() {
      return this.filteredNotifications.length > this.nExtraNotificationIncrements
          && this.notificationSample.length !== this.filteredNotifications.length;
    },
    notificationRouteMap() {
      return {
        thread: notification => ({
          name: this.getThreadPageName(notification.payload.module_id),
          params: {
            moduleId: notification.payload.module_id,
            threadId: notification.payload.root_parent_id,
          },
        }),
        accept_answer: notification => ({
          name: this.getThreadPageName(notification.payload.module_id),
          params: {
            moduleId: notification.payload.module_id,
            threadId: notification.payload.root_parent_id,
          },
        }),
        new_question: notification => ({
          name: this.getThreadPageName(notification.payload.module_id),
          params: {
            moduleId: notification.payload.module_id,
            threadId: notification.payload.thread_id,
          },
        }),
        question_assigned_to_admin: notification => ({
          name: this.getThreadPageName(notification.payload.module_id),
          params: {
            moduleId: notification.payload.module_id,
            threadId: notification.payload.thread_id,
          },
        }),
        meeting_form_submitted: notification => ({
          name: 'module_meeting',
          params: {
            moduleMeetingId: notification.payload.module_meeting_id,
            moduleId: notification.payload.module_id,
          },
        }),
        referred_otj_entry: notification => ({
          name: 'programme_otj_logs',
          params: {
            programmeId: notification.payload.programme_id,
          },
          query: {
            tab: 'referred',
          },
        }),
        approved_otj_entry: notification => ({
          name: 'programme_otj_logs',
          params: {
            programmeId: notification.payload.programme_id,
          },
          query: {
            tab: 'approved',
          },
        }),
      };
    },
  },

  methods: {
    toggleOpen() {
      if (this.isOpen) {
        this.onClose();
      } else {
        this.onOpen();
      }
    },
    onOpen() {
      this.$emit('open');
    },
    onClose() {
      this.nNotificationsShown = this.nExtraNotificationIncrements;
      this.$emit('close');
    },
    showMore() {
      this.nNotificationsShown += this.nExtraNotificationIncrements;
    },
    isRoutable(notification) {
      return Boolean(Object.prototype.hasOwnProperty.call(this.notificationRouteMap, notification.notification_type));
    },
    getThreadPageName(moduleId) {
      if (this.isDashboard) {
        if (this.$permissions.hasPermission('view_knowledge_base')) {
          return moduleId ? 'knowledge_thread' : 'knowledge_detached_thread';
        }
        return 'profile_knowledge_thread';
      }
      return moduleId ? 'knowledge_thread' : 'profile_knowledge_thread';
    },
    getThreadRoute(notification) {
      return this.notificationRouteMap[notification.notification_type](notification);
    },

    handleClickOutside(event) {
      const notifDropdown = this.$refs.notifDropdown;
      const target = event.type === 'focusout' ? event.relatedTarget : event.target;
      if (notifDropdown && !notifDropdown.contains(target)) {
        this.onClose();
      }
    },

    markNotificationAsRead(notification) {
      this.notificationsStore.markNotificationAsRead(notification);
    },

    readAllNotifications() {
      this.notificationsStore.readAllNotifications();
    },

  },
};
</script>
